import { html, nothing, TemplateResult } from "lit-html";
import { LabelComponent } from "@components/common";
import { JtagService } from "@services/jtag-service";
import { LocationTypeaheadTemplate } from '../templates';
import {
  HotKeys,
  getLocationsList,
  removeLocationsList,
  renderLocationsList,
  renderLocationTypeaheadTemplate,
  renderSuggestedLocationTemplate,
  removeSuggestedLocationTemplate
} from '../config';

const setState = (initialState: ILocationsListTemplateState, newState: ILocationsListTemplateState): ILocationsListTemplateState =>
  Object.assign(initialState, newState);

const LocationTypeaheadComponent = async (props: ILocationTypeaheadProps): Promise<TemplateResult> => {

  const { locationsLimit = 3, locationTypeaheadFor, isLocationLabelEnabled, labelText, onLocationChange } = props;

  const initialState: ILocationsListTemplateState = {
    locationsList: [],
    activeLocationIndex: 0,
    locationsLimit,
    locationTypeaheadFor
  }

  const state: ILocationsListTemplateState = { ...initialState };

  const handlers: ILocationTypeaheadEventHandlers = {
    handleClick,
    handleKeyDown,
    handleMouseEnter,
    handleInputChange,
    handleClearWhereInput,
  }

  function handleClick(event: MouseEvent): void {
    const element: HTMLElement = (<HTMLElement>event.target);
    const location: string = element.innerText;

    onLocationChange?.(location);

    removeLocationsList(locationTypeaheadFor);
    renderLocationTypeaheadTemplate({ ...props, locationValue: location }, handlers);
    removeSuggestedLocationTemplate(locationTypeaheadFor);

    setState(state, initialState);
  }

  async function handleKeyDown(event: KeyboardEvent): Promise<void> {
    const { key } = event;

    if (Object.values(HotKeys).includes(key as HotKeys) && state.locationsList.length) {
      event.preventDefault();
      event.stopPropagation();

      switch (key) {
        case HotKeys.ARROW_DOWN:
          if (state.activeLocationIndex < state.locationsList.length - 1) {
            const activeLocationIndex = state.activeLocationIndex + 1;
            setState(state, { ...state, activeLocationIndex });

            renderLocationsList(state, handlers);
          }

          break;

        case HotKeys.ARROW_UP:
          if (state.activeLocationIndex > 0) {
            const activeLocationIndex = state.activeLocationIndex - 1;
            setState(state, { ...state, activeLocationIndex });

            renderLocationsList(state, handlers);
          }

          break;

        case HotKeys.ENTER:
        case HotKeys.TAB:
          onLocationChange?.(state.locationsList[state.activeLocationIndex].canonicalName);

          renderLocationTypeaheadTemplate({ ...props, locationValue: state.locationsList[state.activeLocationIndex].canonicalName }, handlers)
          removeLocationsList(locationTypeaheadFor);
          removeSuggestedLocationTemplate(locationTypeaheadFor);

          break;

        case HotKeys.ESCAPE:
          setState(state, initialState);

          removeLocationsList(locationTypeaheadFor);

          break;

        default:
          break;
      }
    }
  }

  function handleMouseEnter(activeLocationIndex: number, locationsList: IAutocompleteLocationProvider[]): void {
    setState(state, { ...state, activeLocationIndex, locationsList });

    renderLocationsList(state, handlers);
  }

  async function handleInputChange(event: Event): Promise<void> {
    const location = (<HTMLInputElement>event.target).value.trim();

    if (location.length) {
      onLocationChange?.(location);
      const locationsList: IAutocompleteLocationProvider[] = await getLocationsList(location, locationsLimit);

      if (locationsList.length) {
        setState(state, { ...state, locationsList });

        renderSuggestedLocationTemplate(locationsList[0].canonicalName, locationTypeaheadFor)

        renderLocationsList(state, handlers);
      } else {
        removeLocationsList(locationTypeaheadFor);
        removeSuggestedLocationTemplate(locationTypeaheadFor);
        setState(state, initialState);
      }
    } else {
      removeLocationsList(locationTypeaheadFor);
      removeSuggestedLocationTemplate(locationTypeaheadFor);
      setState(state, initialState);
    }
  }

  function handleClearWhereInput(): void {
    onLocationChange?.("");

    JtagService.trackCustomEvent('subscription.modal.clean.where.by.ip.input');

    renderLocationTypeaheadTemplate({ ...props, locationValue: "" }, handlers);
    removeSuggestedLocationTemplate(locationTypeaheadFor);
    removeLocationsList(locationTypeaheadFor);

    setState(state, initialState);
  }

  function template(): TemplateResult {
    return html`
    ${isLocationLabelEnabled ? html`${LabelComponent({
      cssClasses: "proposal-form__label",
      labelText
    })}` : nothing}
      <div class="location-typeahead-wrapper ts-location-typeahead-wrapper-${locationTypeaheadFor}">
        ${LocationTypeaheadTemplate(props, handlers)}
      </div>
      <div class="locations-wrapper ts-suggested-locations-wrapper-${locationTypeaheadFor}"></div>
    `;
  }

  return template();
}

export default LocationTypeaheadComponent;